import { cleanUrl, displayHost } from '../utils/url';

export class AnswerSource {
  url!: string;
  title: string | null = null;
  publishedAt: string | null = null;
  domainName: string | null = null;

  constructor(data: any = {}) {
    this.url = cleanUrl(data.url) ?? '';
    this.title = typeof data.title === 'string' ? data.title.replace(/\s+/g, ' ').trim() || null : null;
    this.publishedAt = data.published_at ?? null;
    this.domainName = data.domain_name || null;
  }

  /** The domain to show, without "www.". */
  get host(): string {
    return displayHost(this.domainName, this.url);
  }

  /** No favicon on /answer sources; here so a source fits Top sites. */
  readonly favicon = null;
}

/** One statement the structured answer rests on (`answer_claim`). */
export class AnswerClaim {
  statement!: string;
  /** URLs backing it; empty when nothing retrieved supported it. */
  sourceUrls: string[] = [];

  constructor(data: any = {}) {
    this.statement = data.statement ?? '';
    this.sourceUrls = (data.source_urls ?? []).map(cleanUrl).filter((u: string | null): u is string => !!u);
  }
}

/** The answer object when output_type is structured (`structured_answer`). */
export class StructuredAnswer {
  text: string | null = null;
  /** The model's own estimate, 0–1. */
  confidence: number | null = null;
  claims: AnswerClaim[] = [];

  constructor(data: any = {}) {
    this.text = typeof data.text === 'string' ? data.text : null;
    this.confidence = typeof data.confidence === 'number' ? data.confidence : null;
    this.claims = (data.claims ?? []).map((c: object) => new AnswerClaim(c));
  }
}

/** One retrieval in reasoning mode (`meta.searches`; not in the docs yet). */
export class AnswerSearch {
  query!: string;
  passagesFound: number | null = null;
  /** Passages this search added that earlier ones hadn't found. */
  newPassages: number | null = null;

  constructor(data: any = {}) {
    this.query = data.query ?? '';
    this.passagesFound = typeof data.passages_found === 'number' ? data.passages_found : null;
    this.newPassages = typeof data.new_passages === 'number' ? data.new_passages : null;
  }
}

export class AnswerResultMeta {
  mode: string | null = null;
  outputType: string | null = null;
  sourceCount: number | null = null;
  /** Structured only: false = the model replied but it wouldn't parse; null = not asked. */
  answerParsed: boolean | null = null;
  /** Reasoning mode only: the retrievals it ran, in order. */
  searches: AnswerSearch[] = [];
  parametersEcho: Record<string, unknown> = {};

  constructor(data: any = {}) {
    this.mode = data.mode ?? null;
    this.outputType = data.output_type ?? null;
    this.sourceCount = typeof data.source_count === 'number' ? data.source_count : null;
    this.answerParsed = typeof data.answer_parsed === 'boolean' ? data.answer_parsed : null;
    this.searches = (data.searches ?? []).map((s: object) => new AnswerSearch(s));
    this.parametersEcho = data.parameters_echo ?? {};
  }
}

/** The data object of an /answer response (`answer_result`). `answer` is
 *  prose when output_type is unstructured, an object when structured, and
 *  null when nothing came back; `text` is the prose either way. */
export class AnswerResult {
  question!: string;
  text: string | null = null;
  structured: StructuredAnswer | null = null;
  sources: AnswerSource[] = [];
  meta!: AnswerResultMeta;

  constructor(data: any = {}) {
    this.question = data.question;
    if (typeof data.answer === 'string') {
      this.text = data.answer;
    } else if (data.answer && typeof data.answer === 'object') {
      this.structured = new StructuredAnswer(data.answer);
      this.text = this.structured.text;
    }
    this.sources = (data.sources ?? []).map((s: object) => new AnswerSource(s));
    this.meta = new AnswerResultMeta(data.meta ?? {});
  }
}
